import { useAuthentication } from '@/composables/useAuthentication';
import { useUserDefinedIndexes } from '@/composables/useUserDefinedIndexes';
import { formatSharpCharts2DateString } from '@/utils/formatSharpCharts2DateString';
import { subtractMonths } from '@/utils/subtractMonths';
import { HISTORICAL_PRICE_DATA_PERIOD_OPTION_DAILY, HISTORICAL_PRICE_DATA_PERIOD_OPTION_WEEKLY, HISTORICAL_PRICE_DATA_PERIOD_OPTION_MONTHLY } from '@/enums/historical-price-data-period-options';

export async function fetchHistoricalPriceData(symbol: string, period: string) {
  const { memberRT, isPro } = useAuthentication();
  const { appendChartCodeToUserDefinedIndexSymbol } = useUserDefinedIndexes();
  let formattedSymbol = symbol;
  let formattedPeriod = period;

  if (isPro.value) {
    const ratioSplitSymbol = symbol.split(':');

    if (ratioSplitSymbol.length === 2) {
      formattedSymbol = `${appendChartCodeToUserDefinedIndexSymbol(ratioSplitSymbol[0])}:${appendChartCodeToUserDefinedIndexSymbol(ratioSplitSymbol[1])}`;
    } else {
      formattedSymbol = appendChartCodeToUserDefinedIndexSymbol(symbol);
    }
  } else {
    formattedPeriod = HISTORICAL_PRICE_DATA_PERIOD_OPTION_DAILY.value;
  }

  let numMonths;

  if (formattedPeriod === HISTORICAL_PRICE_DATA_PERIOD_OPTION_MONTHLY.value) {
    numMonths = 180;
  } else if (formattedPeriod === HISTORICAL_PRICE_DATA_PERIOD_OPTION_WEEKLY.value) {
    numMonths = 60;
  } else {
    numMonths = 12;
  }

  const startDate = subtractMonths(new Date(), numMonths, false);
  const formattedStartDate = formatSharpCharts2DateString(startDate);
  const memberRTCopy = memberRT.value;

  const urlSearchParams = new URLSearchParams({
    ticker: formattedSymbol,
    start: formattedStartDate,
    barwidth: formattedPeriod,
    out: 'text',
    memberrt: memberRTCopy,
    randomNumber: Date.now().toString()
  });

  let responseText: string;

  try {
    const response = await fetch(`/quotebrain/pastdata?${urlSearchParams.toString()}`, {
      method: 'GET'
    });

    if (!response.headers.get('content-type')?.includes('text/plain')) { // API returns json response on error
      throw new Error('Response Content-Type header is not of type text/plain.');
    }

    responseText = await response.text();
  } catch (e) {
    window.sc3WorkbenchLogger.error(`Failed to fetch historical price data for: symbol: ${formattedSymbol} and period: ${period} and startDate: ${formattedStartDate} and memberRT: ${memberRTCopy}.`, e);

    throw new Error(`Failed to fetch historical price data for: symbol: ${formattedSymbol} and period: ${period} and startDate: ${formattedStartDate} and memberRT: ${memberRTCopy}.`, { cause: e });
  }

  if (!responseText) {
    throw new Error(`Failed to fetch historical price data for: symbol: ${formattedSymbol} and period: ${period} and startDate: ${formattedStartDate} and memberRT: ${memberRTCopy} - responseText is an empty string.`);
  }

  if (responseText === `{"error": "${formattedSymbol} not found"}`) {
    throw new Error(`Failed to fetch historical price data for: symbol: ${formattedSymbol} and period: ${period} and startDate: ${formattedStartDate} and memberRT: ${memberRTCopy} - symbol not found.`);
  }

  return responseText;
}
